/**
 * UnscheduleBuffer — a strip rendered below the page header while an
 * existing schedule pill is being dragged. It exposes two drop targets:
 *
 *   ┌─────────────────────────────┬─────────────────┐
 *   │  Zoom out   (day → week     │   Unschedule    │
 *   │             → month)         │   (delete)      │
 *   └─────────────────────────────┴─────────────────┘
 *
 * Behaviour is driven by the parent via `onDragMove` / `onDragEnd`:
 *   - Hovering the zoom-out region for ~500 ms broadens the view (parent
 *     owns that dwell timer so it sees the authoritative `viewType`).
 *   - Dropping on the zoom-out region also zooms (explicit "drop here"
 *     path).
 *   - Dropping on the unschedule region deletes the event. Accidentally
 *     hovering shouldn't nuke an event, so there's no dwell variant.
 *
 * In month view the zoom-out region is hidden (no wider view exists) and
 * the entire strip is just the unschedule target.
 */

import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { Trash2, ZoomOut } from 'lucide-react'

export const BUFFER_DROP_TYPE = 'schedule-buffer'

export type BufferAction = 'zoom_out' | 'unschedule'

export interface BufferDropData {
  kind: typeof BUFFER_DROP_TYPE
  action: BufferAction
}

interface UnscheduleBufferProps {
  /** Whether the zoom-out region is shown (false in month view). */
  canZoomOut: boolean
}

const ZoomOutZone: React.FC = () => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${BUFFER_DROP_TYPE}:zoom_out`,
    data: { kind: BUFFER_DROP_TYPE, action: 'zoom_out' } satisfies BufferDropData,
  })

  return (
    <div
      ref={setNodeRef}
      role="button"
      aria-label="Hold here to zoom out to a wider view"
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-l-md border border-r-0 border-dashed transition-colors ${
        isOver
          ? 'border-brand-primary bg-brand-subtle text-brand-primary'
          : 'border-soft text-muted hover:border-brand-primary/40'
      }`}
    >
      <ZoomOut className="w-4 h-4" />
      <span className="text-sm font-medium">
        {isOver ? 'Hold to zoom out…' : 'Drag here to zoom out'}
      </span>
    </div>
  )
}

const UnscheduleZone: React.FC<{ rounded: 'full' | 'right' }> = ({ rounded }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${BUFFER_DROP_TYPE}:unschedule`,
    data: { kind: BUFFER_DROP_TYPE, action: 'unschedule' } satisfies BufferDropData,
  })
  const radiusCls = rounded === 'full' ? 'rounded-md' : 'rounded-r-md'
  return (
    <div
      ref={setNodeRef}
      role="button"
      aria-label="Drop here to unschedule"
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-dashed transition-colors ${radiusCls} ${
        isOver
          ? 'border-bad bg-bad/10 text-bad'
          : 'border-soft text-muted hover:border-bad/50'
      }`}
    >
      <Trash2 className="w-4 h-4" />
      <span className="text-sm font-medium">
        {isOver ? 'Release to unschedule' : 'Drag here to unschedule'}
      </span>
    </div>
  )
}

export const UnscheduleBuffer: React.FC<UnscheduleBufferProps> = ({ canZoomOut }) => {
  return (
    <div
      role="region"
      aria-label="Drag actions buffer"
      className="flex items-stretch gap-0 px-4 py-2 bg-surface-alt/40 border-b border-soft"
    >
      {canZoomOut && <ZoomOutZone />}
      <UnscheduleZone rounded={canZoomOut ? 'right' : 'full'} />
    </div>
  )
}

export default UnscheduleBuffer
